import '../../mocks/storage.ts';
import { db } from '@qcdoc/mock-runtime/helpers.ts';
import { AUTH_PORTAL, PORTAL, IS_INDEPENDENT_PORTAL } from '../../app/portal.ts';
import type { UserInfo } from '../../types/common/common.ts';
import type { AuthState } from '../../redux/slices/authSlice.ts';
import { AUTH_MODE } from './config.ts';
import { expandAssignedRoles } from './mockRoleGroups.ts';

const TOKEN_STORAGE_KEY = IS_INDEPENDENT_PORTAL ? `qcdoc.${PORTAL}.auth.token` : 'qcdoc.auth.token';
const USER_STORAGE_KEY = IS_INDEPENDENT_PORTAL ? `qcdoc.${PORTAL}.auth.user` : 'qcdoc.auth.user';

export interface StoredSession {
  token: string;
  expiresAt: string;
  user: UserInfo;
}

export function assertSessionPortal(user: UserInfo | null): void {
  if (user && IS_INDEPENDENT_PORTAL && user.portal !== AUTH_PORTAL) {
    throw new Error('This account belongs to another portal.');
  }
}

export function readStoredAuth(): AuthState {
  const empty: AuthState = { user: null, accessToken: null, expiresAt: null, roles: [], isInitialized: false, isLoading: false };
  if (AUTH_MODE === 'entra') return empty;
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    const rawUser = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw || !rawUser) return empty;
    const { token, expiresAt } = JSON.parse(raw) as StoredSession;
    if (typeof token !== 'string' || !token || !Number.isFinite(Date.parse(expiresAt)) || Date.parse(expiresAt) <= Date.now()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      return empty;
    }
    const user = JSON.parse(rawUser) as UserInfo;
    if (!user || typeof user.sub !== 'string') return empty;
    assertSessionPortal(user);
    const usesSharedMockService = IS_INDEPENDENT_PORTAL && Boolean(window.__QCDOC_PORTAL_CONFIG__?.mockServiceUrl);
    if (IS_INDEPENDENT_PORTAL && !usesSharedMockService) {
      const account = db().users.find((record) => token === `mock-session:${record.userId}`);
      if (!account || account.portal !== AUTH_PORTAL || account.userId !== user.sub
        || !account.isApproved || account.isLockedOut) return empty;
      user.groups = expandAssignedRoles(account.roles);
    }
    return { ...empty, user, accessToken: token, expiresAt, roles: user.groups ?? [], isInitialized: true };
  } catch {
    return empty;
  }
}

export function persistAuth(state: AuthState): void {
  try {
    if (AUTH_MODE === 'entra') {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      return;
    }
    if (state.accessToken) localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ token: state.accessToken, expiresAt: state.expiresAt }));
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
    if (state.user) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(state.user));
    else localStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    return;
  }
}